import { useQuery } from '@apollo/client';
import type { PractitionerRole } from '@medplum/fhirtypes';
import { useMemo, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Card,
  EluveCardContent,
  EluveCardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useFhirRestRead, useFhirSearch } from '@eluve/fhir-data';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
} from '@eluve/fhir-renderer';
import {
  FHIR_USER_ID_SYSTEM,
  FHIR_USER_LOCATION_ID_SYSTEM,
  PRACTITIONER_ROLE_CODES,
  type PractitionerRoleCode,
  practitionerRoleCodeOf,
  userLocationIdentifierValue,
} from '@eluve/fhir-utils';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { getMemberLocationsQuery } from './operations';

const ROLE_LABELS: Record<PractitionerRoleCode, string> = {
  clinician: 'Clinician',
  'front-desk': 'Front desk',
};

/** The role code carried by the roles stamped with one specific identifier. */
function roleCodeForIdentifier(
  roles: readonly PractitionerRole[],
  system: string,
  value: string,
): PractitionerRoleCode | undefined {
  return practitionerRoleCodeOf(
    roles.filter((role) =>
      role.identifier?.some(
        (identifier) =>
          identifier.system === system && identifier.value === value,
      ),
    ),
  );
}

// Radix Select items can't carry an empty value, so "no role" rides a sentinel.
const UNSET_VALUE = '__unset__';

const RoleSelect: React.FC<{
  value: PractitionerRoleCode | undefined;
  /** Label for the no-role option (and the trigger when nothing is set). */
  unsetLabel: string;
  disabled: boolean;
  onChange: (next: PractitionerRoleCode | null) => void;
}> = ({ value, unsetLabel, disabled, onChange }) => (
  <Select
    value={value ?? UNSET_VALUE}
    disabled={disabled}
    onValueChange={(next) => {
      const parsed =
        next === UNSET_VALUE ? null : (next as PractitionerRoleCode);
      if (parsed !== (value ?? null)) onChange(parsed);
    }}
  >
    <SelectTrigger>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={UNSET_VALUE}>{unsetLabel}</SelectItem>
      {PRACTITIONER_ROLE_CODES.map((code) => (
        <SelectItem key={code} value={code}>
          {ROLE_LABELS[code]}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Practice roles: the org-wide default plus a per-location override for each
 * location the member is assigned to ("front desk here, clinician there").
 * Reads resolve per-location first, then the org default, then clinician —
 * mirroring how the backend derives access entries. Writes go through the
 * backend endpoint onto eluve-owned PractitionerRoles only; roles synced from
 * an external system are read, never edited.
 */
const RoleSection: React.FC<{
  userId: string;
  practitionerFhirId: string;
}> = ({ userId, practitionerFhirId }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const {
    data: roles,
    loading,
    error: rolesError,
    refetch,
  } = useFhirRestRead(
    async (client) => [
      ...(await client.searchResources(
        'PractitionerRole',
        { practitioner: `Practitioner/${practitionerFhirId}` },
        // Bypass the SDK's GET cache so refetch() after a role write
        // actually re-reads — the cached bundle predates the write.
        { cache: 'no-cache' },
      )),
    ],
    [practitionerFhirId],
  );

  const { data: locationData } = useQuery(getMemberLocationsQuery, {
    variables: { tenantId, userId },
  });
  const assignments = locationData?.locationUsers ?? [];

  const orgRole = roleCodeForIdentifier(
    roles ?? [],
    FHIR_USER_ID_SYSTEM,
    userId,
  );

  const setRole = async (
    role: PractitionerRoleCode | null,
    locationId?: string,
  ) => {
    setSaving(true);
    try {
      const result = await apiClient.medplum.setPractitionerRole({
        params: { tenantId, userId },
        body: { role, locationId },
      });
      if (result.status === 200) {
        const label = role ? `Role set to ${ROLE_LABELS[role]}.` : 'Role cleared.';
        // The role stuck but the member still holds their previous
        // permissions — say so, because a plain success would read as though
        // the downgrade had taken effect.
        if (result.body.accessSyncFailed) {
          toast.warning(
            `${label} Their access permissions did not update — use Re-sync policies.`,
          );
        } else {
          toast.success(label);
        }
        refetch();
      } else {
        const error =
          result.status === 404 || result.status === 422
            ? result.body.error
            : 'unknown error';
        toast.error(`Couldn't set role: ${error}`);
      }
    } catch {
      toast.error('Failed to reach API');
    } finally {
      setSaving(false);
    }
  };

  // A failed roles read must block edits: writing against unseen state could
  // silently replace an existing role.
  const busy = loading || saving || Boolean(rolesError);
  const defaultLabel = orgRole ? ROLE_LABELS[orgRole] : ROLE_LABELS.clinician;

  return (
    <Stack gap={3} align="stretch" className="max-w-md">
      {rolesError && (
        <Text size="xs" className="text-destructive">
          Couldn't load practice roles — editing is disabled.
        </Text>
      )}
      <Stack gap={1} align="stretch">
        <Text size="xs" color="tertiary">
          Practice role (default)
        </Text>
        <RoleSelect
          value={orgRole}
          unsetLabel="Not set (Clinician)"
          disabled={busy}
          onChange={(next) => void setRole(next)}
        />
        <Text size="xs" color="tertiary">
          Front desk books appointments; clinicians also own clinical
          documentation. FHIR-side only — the Eluve role above is separate.
        </Text>
      </Stack>
      {assignments.length > 0 && (
        <Stack gap={2} align="stretch">
          <Text size="xs" color="tertiary">
            Overrides by location
          </Text>
          {assignments.map((assignment) => (
            <Stack
              key={assignment.locationId}
              direction="horizontal"
              gap={2}
              align="center"
              justify="between"
            >
              <Text inline size="sm" className="min-w-0 truncate">
                {assignment.location.name}
              </Text>
              <div className="w-44 shrink-0">
                <RoleSelect
                  value={roleCodeForIdentifier(
                    roles ?? [],
                    FHIR_USER_LOCATION_ID_SYSTEM,
                    userLocationIdentifierValue(userId, assignment.locationId),
                  )}
                  unsetLabel={`Default (${defaultLabel})`}
                  disabled={busy}
                  onChange={(next) => void setRole(next, assignment.locationId)}
                />
              </div>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The member's FHIR mirror, for tenant admins: the mirrored Practitioner
 * (written by the `sync_fhir_mirror` job) plus the practice-role selector.
 *
 * Keyed by identifier, not by FHIR id: the mirror's durable key is
 * `https://eluve.com/user-id|<eluve user id>` and no reference is stored on
 * the eluve row.
 */
const MirrorBody: React.FC<{ userId: string }> = ({ userId }) => {
  const { resources, loading, error, enabled } = useFhirSearch('Practitioner', {
    identifier: `${FHIR_USER_ID_SYSTEM}|${userId}`,
  });
  const map = useMemo(() => buildResolutionMap([]), []);
  const practitioner = resources[0];

  if (!enabled) {
    return (
      <Text size="sm" color="tertiary">
        FHIR session unavailable.
      </Text>
    );
  }

  if (loading) {
    return (
      <Text size="sm" color="tertiary">
        Loading FHIR resource…
      </Text>
    );
  }

  if (error) {
    return (
      <Text size="sm" color="tertiary">
        Couldn't load the FHIR resource.
      </Text>
    );
  }

  if (!practitioner?.id) {
    return (
      <Text size="sm" color="tertiary">
        Not mirrored yet.
      </Text>
    );
  }

  return (
    <Stack gap={3} align="stretch">
      <FhirResourceScope map={map}>
        <RenderResource resource={practitioner} />
      </FhirResourceScope>
      <RoleSection userId={userId} practitionerFhirId={practitioner.id} />
      <Text size="xs" color="tertiary" className="font-mono">
        Practitioner/{practitioner.id}
      </Text>
    </Stack>
  );
};

export const MemberFhirMirrorCard: React.FC<{ userId: string }> = ({
  userId,
}) => (
  <Card className="w-full rounded-xl border-border-primary">
    <EluveCardHeader>
      <Text>FHIR Mirror</Text>
    </EluveCardHeader>
    <EluveCardContent>
      <MirrorBody userId={userId} />
    </EluveCardContent>
  </Card>
);
